import { AxiosError } from 'axios';
import { Form, FormikHelpers } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { requisitesApi } from 'api';
import {
  CloseDialogResult,
  CloseFormikDialogResult,
  FormikCheckbox,
  FormikDialog,
} from 'components';
import { useMutation, useQueryFilters } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { QueryParams, Requisites, RequisitesFilters } from 'types';

type Values = {
  disableFutureVerification: boolean;
};

type Props = {
  open: boolean;
  count?: number;
  onClose: (result: CloseDialogResult) => void;
};

export const BatchManagerRequisitesVerificationDialog: React.FC<Props> = ({
  open,
  count,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'batch_requisites_verification_dialog',
  });
  const { filtersQuery } = useQueryFilters<RequisitesFilters>();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        disableFutureVerification: Yup.boolean(),
      }),
    [],
  );

  const initialValues: Values = useMemo(
    () => ({
      disableFutureVerification: false,
    }),
    [],
  );

  const mutation = useMutation<
    Requisites,
    AxiosError,
    {
      disableFutureVerification: boolean;
      params: Partial<QueryParams>;
    }
  >(requisitesApi.batchVerify, {
    notifierType: 'execute',
    notifierMessages: {
      success: (data) => t('batch_verify_result', { affected: data?.affected }),
    },
  });

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      mutation.mutate(
        {
          params: { filters: filtersQuery },
          disableFutureVerification: values.disableFutureVerification,
        },
        {
          onSettled: () => {
            formikHelpers.setSubmitting(false);
          },
          onSuccess: () => {
            formikHelpers.resetForm();
            onClose({ ok: true });
          },
        },
      );
    },
    [mutation, filtersQuery, onClose],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (result.ok && result.data) {
        handleSubmit(result.data.values, result.data.formikHelpers);
      } else {
        mutation.reset();
        result.data?.formikHelpers.resetForm();
        onClose({ ok: result.ok });
      }
    },
    [handleSubmit, mutation, onClose],
  );

  return (
    <FormikDialog
      dialogProps={{
        open,
        title: t('title'),
        mutation,
        onClose: handleClose,
      }}
      formikProps={{
        validationSchema,
        initialValues,
        isInitialValid: true,
      }}
    >
      {() => (
        <Form>
          <div className="tw-mb-4">{`${t(
            'selected_requisites_count',
          )}: ${count}`}</div>
          <div>
            <FormikCheckbox
              label={t('fields.disable_future_verification')}
              name="disableFutureVerification"
            />
          </div>
          <div className="tw-text-gray-500">
            {t('fields.disable_future_verification_helper')}
          </div>
        </Form>
      )}
    </FormikDialog>
  );
};
